import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useNotify, useRefresh } from 'react-admin';

import { AdminChangeEmailDocument } from '../../../graphql/types';

interface Props {
  userId: string;
  currentEmail: string;
  open: boolean;
  onClose: () => void;
}

export const ChangeEmailDialog: React.FC<Props> = ({
  userId,
  currentEmail,
  open,
  onClose,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [newEmail, setNewEmail] = React.useState('');
  const [validationError, setValidationError] = React.useState<string>();

  const [changeEmail, { loading, error }] = useMutation(
    AdminChangeEmailDocument,
    {
      onCompleted: () => {
        notify('Email updated successfully', { type: 'success' });
        refresh();
        handleClose();
      },
    },
  );

  const handleClose = () => {
    setNewEmail('');
    setValidationError(undefined);
    onClose();
  };

  const validate = (value: string): string | undefined => {
    if (!value.trim()) {
      return 'Email is required';
    }
    if (value.trim().toLowerCase() === currentEmail.toLowerCase()) {
      return 'New email must be different from the current email';
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = newEmail.trim().toLowerCase();
    const err = validate(normalized);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(undefined);
    try {
      await changeEmail({ variables: { userId, email: normalized } });
    } catch {
      notify('Failed to update email. Please try again.', { type: 'warning' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value);
    if (validationError) {
      setValidationError(validate(e.target.value));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Change Email Address</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Current email: <strong>{currentEmail}</strong>
            </Typography>
          </Box>
          <TextField
            label="New email address"
            value={newEmail}
            onChange={handleChange}
            error={Boolean(validationError)}
            helperText={validationError}
            fullWidth
            autoFocus
            type="email"
            autoComplete="email"
            placeholder={`newname@example.com`}
            disabled={loading}
          />
          {error && !validationError && (
            <Box mt={2}>
              <Typography variant="body2" color="error">
                {error.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
